/** @file Converts unfiltered PNG scanlines (8-bit samples or 1-8 bit palette indices) to straight RGBA. */
import type { PngPass } from './pngPasses';

/** How samples become RGBA: `palette` (4 bytes an entry) for colour type 3, `key` (tRNS) for gray and RGB. */
export interface PngColor {
  colorType: number;
  bitDepth: number;
  /** Samples per pixel: 1 gray or index, 2 gray+alpha, 3 RGB, 4 RGBA. */
  channels: number;
  palette?: Uint8Array;
  /** tRNS colour key as r, g, b (gray repeated): pixels of exactly that colour get alpha 0. */
  key?: readonly [number, number, number];
}

/** One scanline's pixels: source offset, first output offset, output step (4, or 4·dx in an Adam7 pass), count. */
interface RowSpan {
  at: number;
  dst: number;
  step: number;
  count: number;
}

/** Source offsets of r, g, b, a per channel count; -1 = opaque (no alpha sample). */
const OFFSETS: Record<number, readonly [number, number, number, number] | undefined> = {
  1: [0, 0, 0, -1],
  2: [0, 0, 0, 1],
  3: [0, 1, 2, -1],
  4: [0, 1, 2, 3],
};
const NO_KEY = [-1, -1, -1] as const;

const byte = (src: Uint8Array, i: number): number => src[i] ?? 0;
const alphaOf = (src: Uint8Array, s: number, ao: number): number => (ao < 0 ? 255 : byte(src, s + ao));

/** One row of 8-bit samples (gray, gray+alpha, RGB, RGBA) to RGBA, applying the colour key. */
function samplesRow(src: Uint8Array, out: Uint8ClampedArray, row: RowSpan, color: PngColor): void {
  const [ro, go, bo, ao] = OFFSETS[color.channels] ?? [0, 0, 0, -1];
  const [kr, kg, kb] = color.key ?? NO_KEY;
  for (let x = 0, s = row.at, o = row.dst; x < row.count; x++, s += color.channels, o += row.step) {
    const r = byte(src, s + ro);
    const g = byte(src, s + go);
    const b = byte(src, s + bo);
    out[o] = r;
    out[o + 1] = g;
    out[o + 2] = b;
    out[o + 3] = r === kr && g === kg && b === kb ? 0 : alphaOf(src, s, ao);
  }
}

/** One row of `bitDepth`-bit palette indices to RGBA; false when an index is outside the palette. */
function indexRow(src: Uint8Array, out: Uint8ClampedArray, row: RowSpan, color: PngColor): boolean {
  const palette = color.palette ?? new Uint8Array(0);
  const depth = color.bitDepth;
  const mask = (1 << depth) - 1;
  for (let x = 0, o = row.dst; x < row.count; x++, o += row.step) {
    const bit = x * depth;
    const entry = ((byte(src, row.at + (bit >> 3)) >> (8 - depth - (bit & 7))) & mask) * 4;
    if (entry >= palette.length) return false;
    out[o] = byte(palette, entry);
    out[o + 1] = byte(palette, entry + 1);
    out[o + 2] = byte(palette, entry + 2);
    out[o + 3] = byte(palette, entry + 3);
  }
  return true;
}

/** RGBA of a `width` x `height` image from its unfiltered `passes` in `data`; null when an index is outside PLTE. */
export function scanlinesToRgba(data: Uint8Array, passes: readonly PngPass[], width: number, height: number, color: PngColor): Uint8ClampedArray | null {
  const out = new Uint8ClampedArray(width * height * 4);
  let start = 0;
  for (const pass of passes) {
    for (let r = 0; r < pass.height; r++) {
      const row = { at: start + r * (pass.rowBytes + 1) + 1, dst: ((pass.y0 + r * pass.dy) * width + pass.x0) * 4, step: pass.dx * 4, count: pass.width };
      if (color.colorType !== 3) samplesRow(data, out, row, color);
      else if (!indexRow(data, out, row, color)) return null;
    }
    start += pass.height * (pass.rowBytes + 1);
  }
  return out;
}
